/**
 * What `/join#<token>` may learn before anybody has signed in.
 *
 * One call, and it is deliberately not behind a session: the invitation token IS the credential, so
 * this is the only read in the console that a signed-out browser makes on purpose. `api()` omits
 * the bearer header when there is no session, which is what makes that free.
 */
import { ApiError, api } from './client'

export type InvitationPreview = {
  readonly organizationName: string
  /** The address the invitation was sent to. Acceptance binds to it, so the form shows it locked. */
  readonly email: string
  readonly role: string
  readonly expiresAt: string
}

type PreviewResponse = {
  readonly organization_name: string
  readonly email: string
  readonly role: string
  readonly expires_at: string
}

/**
 * `null` when the link will not open — expired, withdrawn, already used, or never issued.
 *
 * One value for all four, because the API answers one for all four: which kind of wrong it is, is a
 * fact about somebody else's link. Anything else — a cell with no database, a rate limit, a
 * network failure — is thrown, because those are states a person can act on differently.
 */
export async function previewInvitation(token: string): Promise<InvitationPreview | null> {
  try {
    const row = await api<PreviewResponse>('/v1/invitations/preview', {
      method: 'POST',
      body: { token },
    })
    return {
      organizationName: row.organization_name,
      email: row.email,
      role: row.role,
      expiresAt: row.expires_at,
    }
  } catch (error) {
    if (error instanceof ApiError && error.code === 'invitation_not_open') return null
    throw error
  }
}

/** How a role reads on the join screen. The API's words are keys; these are the sentence. */
export function roleLabel(role: string): string {
  switch (role) {
    case 'owner':
      return 'Owner'
    case 'admin':
      return 'Admin — you can also manage people and branding'
    case 'viewer':
      return 'Viewer — you can see envelopes but not send them'
    case 'billing_admin':
      return 'Billing admin — billing only'
    default:
      return 'Member — you can create and send envelopes'
  }
}
